import { Card, Grid, Select } from 'antd';
import React from 'react';
import useReportLog from '../../../../hooks/useReportLog';
import styled from 'styled-components';
import {
  calculateGpsSummary,
  generateReportSummary,
  toHHMMSS,
} from '../helperFunction';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import useReportSummaryData from '../../../../hooks/useReportSummaryData';
import { EAssetType } from '../../../asset/types/asset.type';
import { EEmailBodyParams } from '../../../masterReport/types/report.type';
import {
  cruiseUnitOptions,
  distanceUnitOptions,
  speedUnitOptions,
} from '../../../../helpers/unit';
import { EReportDataInterval } from '../../types/report-log.type';
import { capitalizeFirstLetter } from '@qlibs/react-components';
import useOperationModeSummary, {
  OperationModeSummaryRow,
} from './useOperationModeSummary';
import useMasterData from '../../../../hooks/useMasterData';
import type { OperationModeSections } from '../../pdf/reportPdfData';

export interface PrecomputedSummary {
  summary: ReturnType<typeof generateReportSummary>;
  gpsSummary: ReturnType<typeof calculateGpsSummary>;
  // the operation modes of the range, for their fuel
  operationMode?: OperationModeSections;
  // the PDF page cannot load the metadata of the asset
  showOperationMode?: boolean;
}

// Operation Mode Clutch Fuel Cons. in the summary and in Total Fuel Cons.
const SHOW_CLUTCH_OPERATION_MODE_FUEL_CONS = false;

const sumConsRate = (rows: OperationModeSummaryRow[]) =>
  rows.reduce((acc, row) => acc + row.consRate, 0);

interface IProps {
  isReportSchedule?: boolean;
  customSummaryProps?: {
    startAt: number;
    endAt: number;
    interval: string;
  };
  // calculated by the backend (GET /report-log/pdf-summary) with the same
  // formulas, so the PDF page does not have to calculate it from the logs
  precomputedSummary?: PrecomputedSummary;
}

const minWidth = 60;

const SummaryTable: React.FC<IProps> = (props) => {
  const screens = Grid.useBreakpoint();
  const {
    logs,
    distanceUnit,
    setDistanceUnit,
    setSpeedUnit,
    speedUnit,
    setCruiseUnit,
    cruiseUnit,
  } = useReportLog();
  const { filter, asset, timezone } = useAppliedReportFilter();
  const { isOperationMode } = useMasterData();

  const summaryRange =
    props.isReportSchedule && props.customSummaryProps
      ? {
          startAt: props.customSummaryProps.startAt,
          endAt: props.customSummaryProps.endAt,
          interval: props.customSummaryProps.interval as EReportDataInterval,
        }
      : {
          startAt: filter.startAt,
          endAt: filter.endAt,
          interval: filter.interval,
        };
  const loadedSummary = useReportSummaryData({ ...summaryRange, timezone });

  // the PDF page gets both from GET /report-log/pdf-data
  const dataSource =
    props.precomputedSummary?.summary || loadedSummary.summary;
  const gpsSummmary =
    props.precomputedSummary?.gpsSummary || loadedSummary.gpsSummary;

  // Same numbers as the Operation Mode tab. The PDF page gets them from the
  // backend, calculated for its operation mode sections, without the logs.
  const operationMode = useOperationModeSummary();
  const precomputedOperationMode = props.precomputedSummary?.operationMode;
  const isOperationModeEnabled =
    props.precomputedSummary?.showOperationMode ||
    asset.metadata?.showOperationMode === 1 ||
    isOperationMode;
  const hasOperationMode = precomputedOperationMode
    ? precomputedOperationMode.engineKeys.length > 0
    : Boolean(logs?.operation_mode && Object.keys(logs.operation_mode).length) &&
      operationMode.rows.length > 0;
  const hasClutchOperationMode = precomputedOperationMode
    ? precomputedOperationMode.clutchEngines.length > 0
    : operationMode.clutch.rows.length > 0;
  const operationModeFuelCons = precomputedOperationMode
    ? sumConsRate(precomputedOperationMode.summaryRows)
    : operationMode.totalConsRate;
  const clutchOperationModeFuelCons = precomputedOperationMode
    ? sumConsRate(precomputedOperationMode.clutchSummaryRows)
    : operationMode.clutch.totalConsRate;
  // the fuel of the operation modes is in the summary and its total, in the
  // CMS and in the PDF
  const showOperationModeFuel = isOperationModeEnabled && hasOperationMode;
  const showClutchOperationModeFuel =
    SHOW_CLUTCH_OPERATION_MODE_FUEL_CONS &&
    isOperationModeEnabled &&
    hasClutchOperationMode;
  // the tables of the modes only in the CMS Summary tab, the PDF/email path
  // renders its own operation mode sections
  const showOperationModeSummary =
    !props.isReportSchedule && showOperationModeFuel;
  const showClutchOperationModeSummary =
    !props.isReportSchedule && showClutchOperationModeFuel;
  const totalFuelCons =
    (dataSource.totalEngineFuelCons || 0) +
    (dataSource.totalAEFuelCons || 0) +
    (showOperationModeFuel ? operationModeFuelCons : 0) +
    (showClutchOperationModeFuel ? clutchOperationModeFuelCons : 0);

  const isMobileSummary = screens.md === false && !props.isReportSchedule;
  const renderMobileUnitSelect = (
    value: string,
    onChange: (value: string) => void,
    options: { label: string; value: string }[],
  ) => (
    <Select
      className='mobile-summary-unit-select'
      size='small'
      value={value}
      onChange={onChange}
      options={options}
      popupMatchSelectWidth={false}
    />
  );

  console.log('dataSource', {dataSource})

  const renderOperationModeSummaryTable = (
    title: string,
    rows: OperationModeSummaryRow[],
    totalRunningTime: number,
    totalConsRate: number,
  ) => (
    <Wrapper column={1}>
      <Table2>
        <tbody>
          <Tr titleSection>
            <Td width={'40%'}>{title}</Td>
            <Td width={'30%'}>Running Time</Td>
            <Td width={'30%'}>Fuel Cons. (Liter)</Td>
          </Tr>
          {rows.map((row) => (
            <Tr key={row.name}>
              <Td width={'40%'}>{capitalizeFirstLetter(row.name)}</Td>
              <Td value width={'30%'}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {toHHMMSS(row.runningTime)}
                </span>
              </Td>
              <Td value width={'30%'}>
                {row.consRate.toFixed(2)}
              </Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td width={'40%'}>Total</Td>
            <Td value width={'30%'}>
              <span style={{ minWidth, display: 'inline-block' }}>
                {toHHMMSS(totalRunningTime)}
              </span>
            </Td>
            <Td value width={'30%'}>
              {totalConsRate.toFixed(2)}
            </Td>
          </Tr>
        </tbody>
      </Table2>
    </Wrapper>
  );

  if (isMobileSummary) {
    return (
      <MobileSummary id='page-summary'>
        <MobileSummarySection title='Fuel Consumption Summary'>
          <MobileSummaryRow
            label='Engine Fuel Cons.'
            value={dataSource.totalEngineFuelCons.toFixed(2)}
            valueId={EEmailBodyParams.total_engine_fuel_cons}
            unit='Liter'
          />
          {showOperationModeFuel && (
            <MobileSummaryRow
              label='Operation Mode Fuel Cons.'
              value={operationModeFuelCons.toFixed(2)}
              unit='Liter'
            />
          )}
          {showClutchOperationModeFuel && (
            <MobileSummaryRow
              label='Operation Mode Clutch Fuel Cons.'
              value={clutchOperationModeFuelCons.toFixed(2)}
              unit='Liter'
            />
          )}
          <MobileSummaryRow
            label='AE Fuel Cons.'
            value={(dataSource.totalAEFuelCons || 0).toFixed(2)}
            valueId={EEmailBodyParams.total_ae_fuel_cons}
            unit='Liter'
          />
          <MobileSummaryRow
            label='Total Fuel Cons.'
            value={totalFuelCons.toFixed(2)}
            valueId={EEmailBodyParams.total_fuel_cons}
            unit='Liter'
            highlight
          />
        </MobileSummarySection>

        <MobileSummarySection title='GPS Summary'>
          <MobileSummaryRow
            label='Total Cruise'
            value={gpsSummmary.totalCruise}
            valueId={EEmailBodyParams.gps_total_cruise}
            unit={renderMobileUnitSelect(
              cruiseUnit,
              (value) => {
                setCruiseUnit(value);
                setDistanceUnit(value);
              },
              cruiseUnitOptions,
            )}
          />
          <MobileSummaryRow
            label='Average Speed'
            value={gpsSummmary.avgSpeed}
            valueId={EEmailBodyParams.gps_average_speed}
            unit={renderMobileUnitSelect(
              speedUnit,
              (value) => setSpeedUnit(value),
              speedUnitOptions,
            )}
          />
          <MobileSummaryRow
            label='Distance'
            value={gpsSummmary.totalDistance}
            valueId={EEmailBodyParams.gps_distance}
            unit={renderMobileUnitSelect(
              distanceUnit,
              (value) => {
                setCruiseUnit(value);
                setDistanceUnit(value);
              },
              distanceUnitOptions,
            )}
          />
        </MobileSummarySection>

        {![EAssetType.EMS].includes(asset.assetType) && (
          <>
            <MobileSummarySection title='Engine Fuel Consumption'>
              <MobileSummaryRow
                label='Port'
                value={dataSource.portTotalFuel.toFixed(2)}
                valueId={EEmailBodyParams.engine_port_fuel_cons}
                unit='Liter'
              />
              <MobileSummaryRow
                label='Starboard'
                value={dataSource.starboardTotalFuel.toFixed(2)}
                valueId={EEmailBodyParams.engine_starboard_fuel_cons}
                unit='Liter'
              />
              <MobileSummaryRow
                label='Center'
                value={
                  dataSource.centerTotalFuel
                    ? dataSource.centerTotalFuel.toFixed(2)
                    : '-'
                }
                valueId={EEmailBodyParams.engine_center_fuel_cons}
                unit='Liter'
              />
            </MobileSummarySection>

            <MobileSummarySection title='Engine Running Time'>
              <MobileSummaryRow
                label='Port'
                value={toHHMMSS(dataSource.portRunningHour)}
                valueId={EEmailBodyParams.engine_port_running_time}
              />
              <MobileSummaryRow
                label='Starboard'
                value={toHHMMSS(dataSource.starboardRunningHour)}
                valueId={EEmailBodyParams.engine_starboard_running_time}
              />
              <MobileSummaryRow
                label='Center'
                value={
                  dataSource.centerRunningHour
                    ? toHHMMSS(dataSource.centerRunningHour)
                    : '-'
                }
                valueId={EEmailBodyParams.engine_center_running_time}
              />
            </MobileSummarySection>

            <MobileSummarySection title='Engine Speed'>
              <MobileSummaryRow
                label='Port'
                value={dataSource.portRPM.toFixed(0)}
                valueId={EEmailBodyParams.engine_port_rpm}
                unit='RPM'
              />
              <MobileSummaryRow
                label='Starboard'
                value={dataSource.starboardRPM.toFixed(0)}
                valueId={EEmailBodyParams.engine_starboard_rpm}
                unit='RPM'
              />
              <MobileSummaryRow
                label='Center'
                value={dataSource.centerRPM ? dataSource.centerRPM.toFixed(0) : '-'}
                valueId={EEmailBodyParams.engine_center_rpm}
                unit='RPM'
              />
            </MobileSummarySection>
          </>
        )}

        <MobileSummarySection title='AE Fuel Consumption'>
          <MobileSummaryRow
            label='AE1'
            value={(dataSource.ae1TotalFuel || 0).toFixed(2)}
            valueId={EEmailBodyParams.ae_1fuel_cons}
            unit='Liter'
          />
          <MobileSummaryRow
            label='AE2'
            value={(dataSource.ae2TotalFuel || 0).toFixed(2)}
            valueId={EEmailBodyParams.ae_2fuel_cons}
            unit='Liter'
          />
          <MobileSummaryRow
            label='AE3'
            value={(dataSource.ae3TotalFuel || 0).toFixed(2)}
            valueId={EEmailBodyParams.ae_3fuel_cons}
            unit='Liter'
          />
          <MobileSummaryRow
            label='Total AE Fuel Cons.'
            value={(
              (dataSource.ae1TotalFuel || 0) +
              (dataSource.ae2TotalFuel || 0) +
              (dataSource.ae3TotalFuel || 0)
            ).toFixed(2)}
            unit='Liter'
            highlight
          />
        </MobileSummarySection>

        <MobileSummarySection title='AE Fuel Consumption'>
          <MobileSummaryRow
            label='AE1'
            value={toHHMMSS(dataSource.ae1RunningHour || 0)}
            valueId={EEmailBodyParams.ae_1running_time}
            unit='Liter'
          />
          <MobileSummaryRow
            label='AE2'
            value={toHHMMSS(dataSource.ae2RunningHour || 0)}
            valueId={EEmailBodyParams.ae_2running_time}
            unit='Liter'
          />
          <MobileSummaryRow
            label='AE3'
            value={toHHMMSS(dataSource.ae3RunningHour || 0)}
            valueId={EEmailBodyParams.ae_3running_time}
            unit='Liter'
          />
          <MobileSummaryRow
            label='Total AE Running Time'
            value={toHHMMSS(
              (dataSource.ae1RunningHour || 0) +
                (dataSource.ae2RunningHour || 0) +
                (dataSource.ae3RunningHour || 0),
            )}
            unit='Liter'
            highlight
          />
        </MobileSummarySection>

        {showOperationModeSummary && (
          <MobileSummaryTable>
            <MobileSummarySectionHeader>Operation Mode</MobileSummarySectionHeader>
            {operationMode.rows.map((row) => (
              <MobileSummaryRow
                key={row.name}
                label={capitalizeFirstLetter(row.name)}
                value={toHHMMSS(row.runningTime)}
                unit={`${row.consRate.toFixed(2)} Liter`}
              />
            ))}
            <MobileSummaryRow
              label='Total'
              value={toHHMMSS(operationMode.totalRunningTime)}
              unit={`${operationMode.totalConsRate.toFixed(2)} Liter`}
              highlight
            />
          </MobileSummaryTable>
        )}
        {showClutchOperationModeSummary && (
          <MobileSummaryTable>
            <MobileSummarySectionHeader>
              Operation Mode Clutch
            </MobileSummarySectionHeader>
            {operationMode.clutch.rows.map((row) => (
              <MobileSummaryRow
                key={row.name}
                label={capitalizeFirstLetter(row.name)}
                value={toHHMMSS(row.runningTime)}
                unit={`${row.consRate.toFixed(2)} Liter`}
              />
            ))}
            <MobileSummaryRow
              label='Total'
              value={toHHMMSS(operationMode.clutch.totalRunningTime)}
              unit={`${operationMode.clutch.totalConsRate.toFixed(2)} Liter`}
              highlight
            />
          </MobileSummaryTable>
        )}
      </MobileSummary>
    );
  }

  return (
    <div id='page-summary'>
      <Wrapper column={2}>
        <Table2>
          <tbody>
            <Tr titleSection>
              <Td colSpan={6}>Fuel Consumption Summary</Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>Engine Fuel Cons.</Td>
              <Td value id={EEmailBodyParams.total_engine_fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {dataSource.totalEngineFuelCons.toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
            {showOperationModeFuel && (
              <Tr>
                <Td width={'50%'}>Operation Mode Fuel Cons.</Td>
                <Td value>
                  <span style={{ minWidth, display: 'inline-block' }}>
                    {operationModeFuelCons.toFixed(2)}
                  </span>
                </Td>
                <Td width={'50%'}>
                  <Label>Liter</Label>
                </Td>
              </Tr>
            )}
            {showClutchOperationModeFuel && (
              <Tr>
                <Td width={'50%'}>Operation Mode Clutch Fuel Cons.</Td>
                <Td value>
                  <span style={{ minWidth, display: 'inline-block' }}>
                    {clutchOperationModeFuelCons.toFixed(2)}
                  </span>
                </Td>
                <Td width={'50%'}>
                  <Label>Liter</Label>
                </Td>
              </Tr>
            )}
            <Tr>
              <Td width={'50%'}>AE Fuel Cons.</Td>
              <Td value id={EEmailBodyParams.total_ae_fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {(dataSource.totalAEFuelCons || 0).toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
            <Tr titleSection>
              <Td width={'50%'}>Total Fuel Cons.</Td>
              <Td value id={EEmailBodyParams.total_fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {totalFuelCons.toFixed(2)}{' '}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
          </tbody>
        </Table2>
        <Table2>
          <tbody>
            <Tr titleSection>
              <Td colSpan={6}>GPS Summary</Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>Total Cruise</Td>
              <Td value id={EEmailBodyParams.gps_total_cruise}>
                {gpsSummmary.totalCruise}
              </Td>
              <Td width={'50%'}>
                {!props.isReportSchedule ? (
                  <Select
                    size='small'
                    value={cruiseUnit}
                    style={{ width: 80 }}
                    onChange={(e) => {
                      setCruiseUnit(e);
                      setDistanceUnit(e);
                    }}
                    options={cruiseUnitOptions}
                  />
                ) : (
                  cruiseUnit
                )}
              </Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>Average Speed</Td>
              <Td value id={EEmailBodyParams.gps_average_speed}>
                {gpsSummmary.avgSpeed}
              </Td>
              <Td width={'50%'}>
                {!props.isReportSchedule ? (
                  <Select
                    size='small'
                    value={speedUnit}
                    style={{ width: 80 }}
                    onChange={(e) => setSpeedUnit(e)}
                    options={speedUnitOptions}
                  />
                ) : (
                  speedUnit
                )}
              </Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>Distance</Td>
              <Td value id={EEmailBodyParams.gps_distance}>
                {gpsSummmary.totalDistance}
              </Td>
              <Td width={'50%'}>
                {!props.isReportSchedule ? (
                  <Select
                    size='small'
                    value={distanceUnit}
                    style={{ width: 80 }}
                    onChange={(e) => {
                      setCruiseUnit(e);
                      setDistanceUnit(e);
                    }}
                    options={distanceUnitOptions}
                  />
                ) : (
                  distanceUnit
                )}
              </Td>
            </Tr>
          </tbody>
        </Table2>
      </Wrapper>

      {![EAssetType.EMS].includes(asset.assetType) && (
        <Wrapper column={2}>
          <Table2 style={{ flex: 1, height: 90 }}>
            <tbody>
              <Tr titleSection>
                <Td colSpan={6}>Engine Fuel Consumption</Td>
              </Tr>
              <Tr>
                <Td width={'50%'}>Port</Td>
                <Td value id={EEmailBodyParams.engine_port_fuel_cons}>
                  <span style={{ minWidth, display: 'inline-block' }}>
                    {dataSource.fmportTotalFuel
                      ? dataSource.fmportTotalFuel.toFixed(2)
                      : '-'}
                  </span>
                </Td>
                <Td width={'50%'}>
                  <Label>Liter</Label>
                </Td>
              </Tr>
              <Tr>
                <Td width={'50%'}>Starboard</Td>
                <Td value id={EEmailBodyParams.engine_starboard_fuel_cons}>
                  <span style={{ minWidth, display: 'inline-block' }}>
                   {dataSource.fmstarboardTotalFuel
                      ? dataSource.fmstarboardTotalFuel.toFixed(2)
                      : '-'}
                  </span>
                </Td>
                <Td width={'50%'}>
                  <Label>Liter</Label>
                </Td>
              </Tr>
              <Tr>
                <Td width={'50%'}>Center</Td>
                <Td value id={EEmailBodyParams.engine_starboard_fuel_cons}>
                  <span style={{ minWidth, display: 'inline-block' }}>
                    {dataSource.fmcenterTotalFuel
                      ? dataSource.fmcenterTotalFuel.toFixed(2)
                      : '-'}
                  </span>
                </Td>
                <Td width={'50%'}>
                  <Label>Liter</Label>
                </Td>
              </Tr>
            </tbody>
          </Table2>

          <EngineMetricTables>
            <Table2>
              <tbody>
                <Tr titleSection>
                  <Td colSpan={6}>Engine Running Time</Td>
                </Tr>
                <Tr>
                  <Td>Port</Td>
                  <Td value id={EEmailBodyParams.engine_port_running_time}>
                    {toHHMMSS(dataSource.portRunningHour)}
                  </Td>
                </Tr>
                <Tr>
                  <Td>Starboard</Td>
                  <Td value id={EEmailBodyParams.engine_starboard_running_time}>
                    {toHHMMSS(dataSource.starboardRunningHour)}
                  </Td>
                </Tr>
                <Tr>
                  <Td>Center</Td>
                  <Td value id={EEmailBodyParams.engine_center_running_time}>
                    {dataSource.centerRunningHour
                      ? toHHMMSS(dataSource.centerRunningHour)
                      : '-'}
                  </Td>
                </Tr>
              </tbody>
            </Table2>

            <Table2>
              <tbody>
                <Tr titleSection>
                  <Td colSpan={6}>Engine Speed</Td>
                </Tr>
                <Tr>
                  <Td width={'50%'}>Port</Td>
                  <Td value id={EEmailBodyParams.engine_port_rpm}>
                    <span style={{ minWidth, display: 'inline-block' }}>
                      {dataSource.portRPM.toFixed(2)}
                    </span>
                  </Td>
                  <Td width={'50%'}>
                    <Label>RPM</Label>
                  </Td>
                </Tr>
                <Tr>
                  <Td width={'50%'}>Starboard</Td>
                  <Td value id={EEmailBodyParams.engine_starboard_rpm}>
                    <span style={{ minWidth, display: 'inline-block' }}>
                      {dataSource.starboardRPM.toFixed(2)}
                    </span>
                  </Td>
                  <Td width={'50%'}>
                    <Label>RPM</Label>
                  </Td>
                </Tr>
                <Tr>
                  <Td width={'50%'}>Center</Td>
                  <Td value id={EEmailBodyParams.engine_center_rpm}>
                    <span style={{ minWidth, display: 'inline-block' }}>
                      {dataSource.centerRPM
                        ? dataSource.centerRPM.toFixed(2)
                        : '-'}
                    </span>
                  </Td>
                  <Td width={'50%'}>
                    <Label>RPM</Label>
                  </Td>
                </Tr>
              </tbody>
            </Table2>
          </EngineMetricTables>
        </Wrapper>
      )}

      <Wrapper>
        <Table2>
          <tbody>
            <Tr titleSection>
              <Td colSpan={6}>AE Fuel Consumption</Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>AE1</Td>
              <Td value id={EEmailBodyParams.ae_1fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {(dataSource.ae1TotalFuel || 0).toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>AE2</Td>
              <Td value id={EEmailBodyParams.ae_2fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {(dataSource.ae2TotalFuel || 0).toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
            <Tr>
              <Td width={'50%'}>AE3</Td>
              <Td value id={EEmailBodyParams.ae_3fuel_cons}>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {(dataSource.ae3TotalFuel || 0).toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
            <Tr titleSection>
              <Td width={'50%'}>Total AE Fuel Cons.</Td>
              <Td value>
                <span style={{ minWidth, display: 'inline-block' }}>
                  {(
                    (dataSource.ae1TotalFuel || 0) +
                    (dataSource.ae2TotalFuel || 0) +
                    (dataSource.ae3TotalFuel || 0)
                  ).toFixed(2)}
                </span>
              </Td>
              <Td width={'50%'}>
                <Label>Liter</Label>
              </Td>
            </Tr>
          </tbody>
        </Table2>

        <Table2>
          <tbody>
            <Tr titleSection>
              <Td colSpan={6}>AE Running Time</Td>
            </Tr>
            <Tr>
              <Td>AE1</Td>
              <Td value id={EEmailBodyParams.ae_1running_time}>
                {toHHMMSS(dataSource.ae1RunningHour || 0)}
              </Td>
            </Tr>
            <Tr>
              <Td>AE2</Td>
              <Td value id={EEmailBodyParams.ae_2running_time}>
                {toHHMMSS(dataSource.ae2RunningHour || 0)}
              </Td>
            </Tr>
            <Tr>
              <Td>AE3</Td>
              <Td value id={EEmailBodyParams.ae_3running_time}>
                {toHHMMSS(dataSource.ae3RunningHour || 0)}
              </Td>
            </Tr>
            <Tr titleSection>
              <Td>Total AE Running Time</Td>
              <Td value>
                {toHHMMSS(
                  (dataSource.ae1RunningHour || 0) +
                    (dataSource.ae2RunningHour || 0) +
                    (dataSource.ae3RunningHour || 0),
                )}
              </Td>
            </Tr>
          </tbody>
        </Table2>
      </Wrapper>

      {showOperationModeSummary &&
        renderOperationModeSummaryTable(
          'Operation Mode',
          operationMode.rows,
          operationMode.totalRunningTime,
          operationMode.totalConsRate,
        )}
      {showClutchOperationModeSummary &&
        renderOperationModeSummaryTable(
          'Operation Mode Clutch',
          operationMode.clutch.rows,
          operationMode.clutch.totalRunningTime,
          operationMode.clutch.totalConsRate,
        )}
    </div>
  );
};

type MobileSummaryRowProps = {
  label: string;
  value: React.ReactNode;
  valueId?: EEmailBodyParams;
  unit?: React.ReactNode;
  highlight?: boolean;
};

const MobileSummarySection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <MobileSummaryTable>
    <MobileSummarySectionHeader>{title}</MobileSummarySectionHeader>
    {children}
  </MobileSummaryTable>
);

const MobileSummaryRow: React.FC<MobileSummaryRowProps> = ({
  label,
  value,
  valueId,
  unit,
  highlight,
}) => (
  <MobileSummaryDataRow $hasUnit={typeof unit !== 'undefined'} $highlight={highlight}>
    <MobileSummaryLabel>{label}</MobileSummaryLabel>
    <MobileSummaryValue id={valueId}>{value}</MobileSummaryValue>
    {typeof unit !== 'undefined' && <MobileSummaryUnit>{unit}</MobileSummaryUnit>}
  </MobileSummaryDataRow>
);

const MobileSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 8px 20px 24px;
  background: #f4f6f9;
`;

const MobileSummaryTable = styled.div`
  width: 100%;
`;

const MobileSummarySectionHeader = styled.div`
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 10px 12px;
  background: #e8ebf3;
  color: #1e1e1e;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`;

const MobileSummaryDataRow = styled.div<{
  $hasUnit: boolean;
  $highlight?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $hasUnit }) =>
    $hasUnit
      ? 'minmax(0, 130px) minmax(64px, 76px) minmax(0, 1fr)'
      : 'minmax(0, 130px) minmax(0, 1fr)'};
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 8px 12px;
  border-bottom: 1px solid #d9d9d9;
  background: ${({ $highlight }) => ($highlight ? '#e8ebf3' : '#ffffff')};
  color: #1e1e1e;
  font-size: 14px;
  line-height: 1.2;
`;

const MobileSummaryLabel = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 400;
`;

const MobileSummaryValue = styled.div`
  min-width: 0;
  text-align: center;
  font-weight: 600;
  overflow-wrap: anywhere;
  white-space: nowrap;
`;

const MobileSummaryUnit = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  color: #1e1e1e;
  font-weight: 400;

  .mobile-summary-unit-select {
    min-width: 64px;
  }

  .mobile-summary-unit-select .ant-select-selector {
    min-height: 26px !important;
    height: 26px !important;
    border-radius: 4px !important;
    padding: 0 4px !important;
    font-size: 14px;
  }

  .mobile-summary-unit-select .ant-select-selection-item {
    line-height: 24px !important;
    padding-inline-end: 16px !important;
    color: #333333;
    font-size: 14px;
    font-weight: 600;
  }
`;

const Wrapper = styled.div<{ column?: number }>`
  display: grid;
  column-gap: 20px;
  grid-template-columns: ${({ column: gridColumn }) =>
    gridColumn ? `repeat(${gridColumn}, 1fr)` : 'repeat(2, 1fr)'};
  min-height: 160px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
    min-height: 0;
    padding: 0 12px;
  }
`;

const Table2 = styled.table`
  display: inline-table;
  margin-top: 30px;
  width: 100%;

  @media (max-width: 767px) {
    display: table;
    table-layout: fixed;
    border-collapse: collapse;
    margin-top: 8px;
    width: 100%;
    background: #fff;
    font-size: 14px;
    line-height: 1.2;

    .ant-select {
      min-width: 64px;
    }

    .ant-select-selector {
      min-height: 28px !important;
      height: 28px !important;
      border-radius: 4px !important;
      padding: 0 4px !important;
      font-size: 12px;
    }

    .ant-select-selection-item {
      line-height: 26px !important;
      padding-inline-end: 16px !important;
      font-weight: 600;
    }
  }
`;

const Tr = styled.tr<{ titleSection?: boolean; highlight?: boolean }>`
  background-color: ${({ titleSection }) =>
    titleSection ? 'var(--gray-0)' : 'white'};
  font-weight: ${({ titleSection }) => (titleSection ? 'bold' : 'normal')};

  @media (max-width: 767px) {
    background-color: ${({ titleSection }) =>
      titleSection ? '#e8ebf3' : '#fff'};
    font-weight: ${({ titleSection }) => (titleSection ? 600 : 400)};
  }
`;

const Td = styled.td<{
  unit?: boolean;
  value?: boolean;
  width?: string;
}>`
  border-top: thin solid #ddd;
  border-bottom: thin solid #ddd;
  width: ${({ width }) => width || '10%'};
  padding: 4px 8px;
  font-weight: ${({ value }) => (value ? 'bold' : 'regular')};
  color: ${({ unit }) => (unit ? 'gray' : 'black')};
  height: 32px !important;

  @media (max-width: 767px) {
    height: 42px !important;
    padding: 0 8px;
    border-top: 0;
    border-bottom: 1px solid #d9d9d9;
    color: #1e1e1e;
    font-size: 14px;
    vertical-align: middle;
    white-space: normal;

    &:first-child {
      width: 44%;
      padding-left: 12px;
    }

    &:nth-child(2) {
      width: 24%;
      text-align: center;
      font-weight: 600;
    }

    &:last-child {
      width: 32%;
      padding-right: 12px;
    }

    &[colspan] {
      width: auto;
      height: 42px !important;
      padding: 0 12px;
      text-align: left;
      font-weight: 600;
    }

    span[style] {
      min-width: 0 !important;
    }
  }
`;

const EngineMetricTables = styled.div`
  flex: 1;
  display: flex;
  gap: 20px;

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const CardInfo = styled(Card)`
  background: var(--gray-2);
  .ant-card-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 10px;

    div {
      :first-child {
        opacity: 0.7;
      }
      :last-child {
        font-weight: bold;
      }
    }
  }
  .ant-card-body::before,
  .ant-card-body::after {
    display: none;
  }
`;

const Accordion = styled.div`
  margin-top: 15px;
  grid-column: 4;
`;

const CardInfoTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 10px;
`;

const CardInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr) 40px;
  gap: 20px;
`;

const CardInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Value = styled.div`
  font-size: 14px;
`;

const Label = styled.span`
  /* font-size: 12px; */
  opacity: 0.8;
  font-weight: 400;
  margin-left: 10px;

  @media (max-width: 767px) {
    margin-left: 0;
    opacity: 1;
  }
`;

export default SummaryTable;
